import React, { useState, useContext, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash, faCheck, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import AuthContext from '../contexts/AuthContext';
import authService from '../services/authService';
import { GoogleLogin } from '@react-oauth/google';
export default function LoginScreen({ onLogin, onForgotPassword, onSignUp, isAuthenticated }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [backgroundImage, setBackgroundImage] = useState(null);
  const { login, googleSignIn } = useContext(AuthContext);
  // Fetch login background from API for this business (ensures per-business isolation)
  useEffect(() => {
    const businessId = localStorage.getItem('businessId');
    if (businessId) {
      authService.getBusinessBackgroundImage(businessId).then((img) => {
        if (img) {
          setBackgroundImage(img);
        } else {
          setBackgroundImage(null);
        }
      }).catch(() => {
        setBackgroundImage(null);
      });
    }
  }, []);
  // Load remembered email on component mount
  useEffect(() => {
    const rememberedEmail = localStorage.getItem('rememberedEmail');
    if (rememberedEmail) {
      setEmail(rememberedEmail);
      setRememberMe(true);
    }
  }, []);
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleLogin();
    }
  };

  const handleLogin = async () => {
    // Clear previous error
    setErrorMessage('');

    // Basic validation
    if (!email.trim() || !password) {
      setErrorMessage('Email and password are required');
      return;
    }

    setLoading(true);

    try {
      // Handle Remember Me email persistence
      if (rememberMe) {
        localStorage.setItem('rememberedEmail', email.trim().toLowerCase());
      } else {
        localStorage.removeItem('rememberedEmail');
      }

      const result = await login(email.trim().toLowerCase(), password, rememberMe);

      // Call parent callback on success with user data
      if (onLogin && result?.user) {
        onLogin(result.user);
      } else if (onLogin) {
        onLogin();
      }
    } catch (error) {
      if (error.message.includes('verify your email')) {
        setErrorMessage('Please verify your email address before signing in. Check your inbox for the verification link.');
      } else {
        setErrorMessage('Email or password incorrect');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setLoading(true);
    setErrorMessage('');

    try {
      const result = await googleSignIn(credentialResponse.credential);

      // Call parent callback on success
      if (onLogin && result?.user) {
        onLogin(result.user);
      } else if (onLogin) {
        onLogin();
      }
    } catch (error) {
      console.error('Google Sign-In failed:', error);
      setErrorMessage(error.message || 'Failed to sign in with Google. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = () => {
    setErrorMessage('Google Sign-In was cancelled or failed. Please try again.');
  };

  return (
    <>
      <div className="center-wrapper">
        <div className="container-1">
          <span className="logo"></span>
          <span className={isAuthenticated ? "title-authenticated" : "title"}>INVEXIS</span>
          {!isAuthenticated && (
            <div className="form">
              <div className="header" style={{ color: "black" }}>Sign In</div>
              <div className="instructions">Sign in to your Invexis account</div>
              <div className="email-wrapper">
                <input
                  className="email-input"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  type="email"
                  autoCapitalize="none"
                  autoComplete="email"
                  name="email"
                />
                <span className="email-icon" aria-hidden="true">
                  <FontAwesomeIcon icon={faEnvelope} />
                </span>
              </div>
              <div className="password-wrapper">
                <input
                  className="password-input"
                  placeholder="Password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyPress={handleKeyPress}
                  autoComplete="current-password"
                  name="password"
                />
                <button
                  className="toggle-password"
                  onClick={(e) => {
                    e.preventDefault();
                    setShowPassword(!showPassword);
                  }}
                  onMouseDown={(e) => e.preventDefault()}
                  type="button"
                >
                  <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                </button>
              </div>

              {errorMessage && (
                <span className="errorText">{errorMessage}</span>
              )}

              <div className="rememberRow">
                <button className="checkbox" onClick={() => setRememberMe(!rememberMe)}>
                  <div className={`checkboxBox ${rememberMe ? 'checkboxChecked' : ''}`}>
                    {rememberMe && <FontAwesomeIcon icon={faCheck} className="tickMark" />}
                  </div>
                  <span className="rememberText">Remember me</span>
                </button>
              </div>

              <button
                className="signInButton"
                onClick={handleLogin}
                disabled={loading}
              >
                {loading ? 'Signing In...' : 'Sign In'}
              </button>
              <button className="forgotText" onClick={onForgotPassword}>
                Forgot password?
              </button>
              <span className="orText">or</span>

              {/* Google Sign-In Button */}
              <div style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                marginBottom: '16px'
              }}>
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={handleGoogleError}
                  theme="outline"
                  size="large"
                  text="signin_with"
                  shape="rectangular"
                  width="100%"
                />
              </div>

              <button className="signUpButton" onClick={onSignUp}>
                Sign Up
              </button>
            </div>
          )}
          <div className="bottomLinks">
            <a href="#" className="link">Terms of Service</a>
            <a href="#" className="link">Privacy Policy</a>
          </div>
        </div>
      </div>
      <style>
        {`
          .center-wrapper {
            height: 100vh !important;
            min-height: unset !important;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #f7f7f7;
          }
          .container-1 {
            max-width: ${backgroundImage ? '600px' : 'none'};
            width: 100vw;
            height: 100vh !important;
            min-height: unset !important;
            padding: 3rem 1rem 0 1rem;
            font-family: Arial, sans-serif;
            background: ${backgroundImage ? `url('${backgroundImage}')` : "#fff"} no-repeat center center;
            background-size: cover;
            background-position: center;
            // box-shadow: 0 2px 8px rgba(0,0,0,0.08);
            --invexis-input-bg: #ffffff;
            position: relative;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            backdrop-filter: blur(4px);
            margin: 0 auto;
          }
          @media (max-width: 768px) {
            .container-1 {
              background-attachment: scroll;
              background-size: cover;
              overflow: auto;
            }
          }
          .logo {
            font-size: 48px;
            display: block;
            margin: 0 auto 8px auto;
            text-align: center;
          }
          .title {
            color: black;
            font-size: 36.8px;
            font-weight: bold;
            letter-spacing: 2px;
            margin-bottom: 32px;
            display: block;
            text-align: center;
          }
          .title-authenticated {
            color: #000;
            font-size: 3rem;
            font-weight: bold;
            text-transform: uppercase;
            letter-spacing: 2px;
            margin-bottom: 32px;
            text-shadow: 
              0px 0px 10px rgba(255, 255, 255, 1),
              0px 0px 20px rgba(255, 255, 255, 0.8),
              0px 0px 30px rgba(255, 255, 255, 0.6);
            display: inline-block; 
            background: rgba(255, 255, 255, 0.15);
            backdrop-filter: blur(1px); 
            -webkit-backdrop-filter: blur(1px);
            padding: 0px 10px; 
            border-radius: 4px;
          }          
          .header {
            font-size: 24px;
            font-weight: bold;
            color: #000;
            margin-bottom: 8px;
            text-align: left;
          }
          .instructions {
            color: #555;
            font-size: 16px;
            margin-bottom: 16px;
            text-align: left;
          }
          .form {
            background-color: transparent;
            padding: 30px;
            border-radius: 16px;
            width: 100%;
            max-width: 400px;
            margin: 0 auto;
            display: flex;
            flex-direction: column;
            margin-bottom: 80px;
            align-items: stretch;
            box-shadow: 0 8px 32px rgba(0,0,0,0.2);
          }
          .input {
            color: black;
            height: 2.8rem;
            border: 2px solid #000 !important;
            border-radius: 8px;
            margin-bottom: 16px;
            padding: 0 12px;
            background-color: #ffffff;
            font-size: 16px;
            width: 100%;
            box-sizing: border-box;
            outline: none;
            transition: none !important;
            transform: none !important;
          }
          .input:focus {
            border: 2px solid #000 !important;
            outline: none;
            transition: none !important;
            transform: none !important;
          }
          .input::placeholder {
            color: #black;
          }
          .email-wrapper {
            position: relative;
            width: 100%;
            margin-bottom: 16px;
            background-color: transparent;
            border: 2px solid #000 !important;
            border-radius: 8px !important;
            display: flex;
            align-items: center;
            height: 48px;
            box-sizing: border-box;
          }
          .email-input {
            height: 100%;
            width: 100%;
            padding: 0 40px 0 12px;
            box-sizing: border-box;
            color: black;
            background-color: #ffffff;
            border: none !important;
            border-radius: 8px;
            font-size: 16px;
            outline: none;
            transition: none !important;
            transform: none !important;
          }
          .email-input:focus {
            outline: none;
            transition: none !important;
            transform: none !important;
          }
          .email-wrapper:focus-within {
            border-color: #000;
          }
          .email-icon {
            position: absolute;
            right: 12px;
            top: 50%;
            transform: translateY(-50%);
            font-size: 18px;
            color: #888;
            padding: 4px;
            width: 24px;
            height: 24px;
            display: flex;
            align-items: center;
            justify-content: center;
            user-select: none;
            pointer-events: none;
          }
          .password-wrapper {
            position: relative;
            width: 100%;
            margin-bottom: 8px;
            background-color: transparent;
            border: 2px solid #000 !important;
            border-radius: 8px !important;
            display: flex;
            align-items: center;
            height: 48px;
          }
          .password-input {
            height: 100%;
            width: 100%;
            padding: 0 40px 0 12px;
            box-sizing: border-box;
            color: black;
            background-color: #ffffff;
            border: none !important;
            border-radius: 8px;
            font-size: 16px;
            outline: none;
            transition: none !important;
            transform: none !important;
          }
          .password-input:focus {
            outline: none;
            transition: none !important;
            transform: none !important;
          }
          .password-wrapper:focus-within {
            border-color: #000;
          }

          /* Prevent browser autofill from changing input background (Chrome/Edge/Safari/Firefox) */
          .container-1 .email-input:-webkit-autofill,
          .container-1 .email-input:-webkit-autofill:hover,
          .container-1 .email-input:-webkit-autofill:focus,
          .container-1 .email-input:-webkit-autofill:active,
          .container-1 .password-input:-webkit-autofill,
          .container-1 .password-input:-webkit-autofill:hover,
          .container-1 .password-input:-webkit-autofill:focus,
          .container-1 .password-input:-webkit-autofill:active {
            background-color: #ffffff !important;
            -webkit-box-shadow: 0 0 0 1000px #ffffff inset !important;
            box-shadow: 0 0 0 1000px #ffffff inset !important;
            -webkit-text-fill-color: #000 !important;
            caret-color: #000 !important;
            transition: background-color 999999s ease-out 0s !important;
          }

          .container-1 .email-input:-moz-autofill,
          .container-1 .password-input:-moz-autofill {
            box-shadow: 0 0 0 1000px #ffffff inset !important;
            background-color: #ffffff !important;
            color: #000 !important;
            caret-color: #000 !important;
          }

          .container-1 .email-input:autofill,
          .container-1 .password-input:autofill {
            box-shadow: 0 0 0 1000px #ffffff inset !important;
            background-color: #ffffff !important;
          }
          .toggle-password {
            position: absolute;
            right: 12px;
            top: 50%;
            transform: translateY(-50%);
            background: none;
            border: none;
            cursor: pointer;
            font-size: 18px;
            color: #888;
            padding: 4px;
            width: 24px;
            height: 24px;
            display: flex;
            align-items: center;
            justify-content: center;
            outline: none !important;
            user-select: none;
            -webkit-tap-highlight-color: transparent;
            transition: none;
            pointer-events: auto;
          }
          .toggle-password * {
            pointer-events: none;
          }
          .toggle-password:hover {
            color: #000;
          }
          .toggle-password:focus {
            outline: none !important;
            box-shadow: none !important;
          }
          .toggle-password:active {
            outline: none !important;
            box-shadow: none !important;
            transform: translateY(-50%);
          }
          /* Override global smoothTransitions.css for auth screen */
          .container-1 input,
          .container-1 button {
            transition: none !important;
          }
          .container-1 input:focus,
          .container-1 input:active,
          .container-1 button:not(.toggle-password):active {
            transform: none !important;
          }
          .container-1 .toggle-password {
            transform: translateY(-50%) !important;
          }
          .container-1 .toggle-password:active {
            transform: translateY(-50%) !important;
          }
          .checkbox,
          .checkboxBox,
          .rememberText {
            transition: none !important;
            transform: none !important;
          }
          .rememberRow {
            display: flex;
            align-items: center;
            margin: 1rem 0;
          }
          .checkbox {
            display: flex;
            align-items: center;
            background: none;
            border: none;
            cursor: pointer;
            padding: 0;
          }
          .checkboxBox {
            width: 18px;
            height: 18px;
            border: 1px solid #000000;
            border-radius: 3px;
            margin-right: 8px;
            display: flex;
            align-items: center;
            justify-content: center;
            position: relative;
            background: none;
          }
          .checkboxChecked {
            border-color: #000000;
          }
          .tickMark {
            color: #000000;
            font-size: 14px;
            line-height: 1;
            position: absolute;
            top: 1px;
            pointer-events: none;
          }
          .rememberText {
            color: #000000ff;
            font-size: 15px;
          }
          .caution {
            text-align:left;
            color: #E2B400;
            font-size: 12px;
            margin-bottom: 16px;
          }
          .signInButton {
            background-color: #000000ff;
            color: #fff;
            padding: 14px 0;
            border-radius: 8px;
            font-size: 16px;
            font-weight: bold;
            text-align: center;
            margin-bottom: 8px;
            cursor: pointer;
            border: none;
            width: 100%;
            transition: background-color 0.3s ease;
          }
          .signInButton:hover:not(:disabled) {
            background-color: #000;
          }
          .signInButton:disabled {
            background-color: #666;
            cursor: not-allowed;
          }
          .forgotText {
            color: #000;
            text-align: center;
            margin-top: 8px;
            text-decoration: underline;
            font-size: 15px;
            background: none;
            border: none;
            cursor: pointer;
          }
          .orText {
            color: #888;
            text-align: center;
            margin: 8px 0;
            font-size: 15px;
            font-weight: bold;
          }
          .signUpButton {
            background-color: #e74c3c;
            color: #fff;
            padding: 14px 0;
            border-radius: 8px;
            font-size: 16px;
            font-weight: bold;
            text-align: center;
            margin-bottom: 8px;
            cursor: pointer;
            border: none;
            width: 100%;
          }
          .signUpButton:hover {
            background-color: #e74c3c;
          }
          .bottomLinks {
            display: flex;
            justify-content: space-between;
            width: 80%;
            position: absolute;
            bottom: 24px;
          }
          .link {
            color: #fff;
            font-size: 13px;
            text-decoration: underline;
          }
          .errorText {
            color: #e74c3c;
            font-size: 13px;
            margin-bottom: 8px;
            margin-top: 4px;
            text-align: center;
          }
        `}
      </style>
    </>
  );
}